import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import {
  Search,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Send,
  X
} from 'lucide-react';

const MyTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState({});
  
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  const sponsorId = localStorage.getItem('sponsorId');
  
  useEffect(() => {
    if (!userId) return;
    
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const allTickets = [];
        const userKey = sponsorId || userId;
        
        // Get all UserTicket data
        const ticketsRef = ref(database, 'UserTicket');
        const snapshot = await get(ticketsRef);
        
        if (snapshot.exists()) {
          // Iterate through all date folders
          snapshot.forEach(dateSnap => {
            const date = dateSnap.key;
            const dateData = dateSnap.val();
            
            // Check if user exists in this date folder
            if (dateData && dateData[userKey]) {
              const userTickets = dateData[userKey];
              
              // Iterate through user's tickets
              Object.keys(userTickets).forEach(ticketKey => {
                const ticket = userTickets[ticketKey];
                
                allTickets.push({
                  id: `${date}/${userKey}/${ticketKey}`,
                  ticketKey: ticketKey,
                  date: date,
                  userKey: userKey,
                  firebasePath: `UserTicket/${date}/${userKey}/${ticketKey}`,
                  replies: ticket.replies || [],
                  ...ticket
                });
              });
            }
          });
        }
        
        // Sort by date (newest first)
        allTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTickets(allTickets);
      } catch (error) {
        console.error('Error fetching tickets:', error);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTickets();
  }, [userId, sponsorId]);
  
  const toggleTicketExpand = (ticketId) => {
    setExpandedTickets(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };
  
  const handleReplyClick = (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
  };
  
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const ticketRef = ref(database, selectedTicket.firebasePath);
      const snapshot = await get(ticketRef);
      
      if (snapshot.exists()) {
        const ticketData = snapshot.val();
        const currentReplies = ticketData.replies || [];
        
        const newReply = {
          id: `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: replyText,
          sender: userId,
          senderName: userName || 'You',
          senderType: 'user',
          timestamp: new Date().toISOString(),
          read: false
        };
        
        const updatedReplies = [...currentReplies, newReply];
        
        await update(ticketRef, {
          replies: updatedReplies,
          lastUpdated: new Date().toISOString()
        });
        
        // Update local state
        const updatedTickets = tickets.map(t => {
          if (t.id === selectedTicket.id) {
            return {
              ...t,
              replies: updatedReplies,
              lastUpdated: new Date().toISOString()
            };
          }
          return t;
        });
        
        setTickets(updatedTickets);
        setSelectedTicket({
          ...selectedTicket,
          replies: updatedReplies,
          lastUpdated: new Date().toISOString()
        });
        
        setReplyText('');
        setSelectedTicket(null); // Close modal after reply
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };
  
  const filteredTickets = tickets.filter(ticket =>
    searchTerm === '' ||
    ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.problemType?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'closed':
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full border border-gray-500/30">Closed</span>;
      case 'in-progress':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">In Progress</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full border border-orange-500/30">Pending</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30">Open</span>;
    }
  };
  
  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/30">Urgent</span>;
      case 'high':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full border border-orange-500/30">High</span>;
      case 'low':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">Low</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30">Medium</span>;
    }
  };
  
  const getProblemTypeIcon = (type) => {
    const icons = {
      technical: '🖥️',
      payment: '💰',
      withdrawal: '💸',
      account: '🔐',
      investment: '📈',
      referral: '👥',
      other: '❓'
    };
    return icons[type] || '📝';
  };
  
  if (loading) {
    return (
      <div className="p-4 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-center mt-2 text-sm text-muted-foreground">Loading tickets...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">My Support Tickets</h1>
        <p className="text-sm text-muted-foreground">View and reply to your support requests</p>
      </div>
      
      {/* Search and New Ticket */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <button
          onClick={() => window.location.href = '/user-dashboard/support/new'}
          className="bg-primary hover:bg-primary/80 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Ticket
        </button>
      </div>
      
      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12 border border-border rounded-lg bg-card">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Tickets Found</h3>
          <p className="text-sm text-muted-foreground mb-6">Create your first support ticket</p>
          <button
            onClick={() => window.location.href = '/user-dashboard/support/new'}
            className="bg-primary hover:bg-primary/80 text-white px-6 py-3 rounded-lg text-sm font-medium transition-colors"
          >
            Create Ticket
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map(ticket => {
            const hasAdminReplies = ticket.replies?.some(r => r.senderType === 'admin');
            const isClosed = ticket.status?.toLowerCase() === 'closed' || ticket.status?.toLowerCase() === 'completed';
            
            return (
              <div key={ticket.id} className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Ticket Summary */}
                <div 
                  className="p-4 cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => toggleTicketExpand(ticket.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-xl">{getProblemTypeIcon(ticket.problemType)}</span>
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{ticket.subject}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          ID: {ticket.ticketId} • Created: {new Date(ticket.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(ticket.status)}
                      {expandedTickets[ticket.id] ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    {getPriorityBadge(ticket.priority)}
                    <span className="bg-secondary/50 px-2 py-1 rounded flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {ticket.replies?.length || 0} replies
                    </span>
                    {hasAdminReplies && (
                      <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded">
                        Support replied
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Expanded View */}
                {expandedTickets[ticket.id] && (
                  <div className="border-t border-border bg-secondary/30 p-4">
                    {/* Original Message */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-foreground bg-primary/10 px-2 py-1 rounded">You</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(ticket.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-3">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.description}</p>
                      </div>
                    </div>
                    
                    {/* All Replies */}
                    {ticket.replies && ticket.replies.length > 0 && (
                      <div className="space-y-4 mb-4">
                        <h4 className="text-xs font-medium text-muted-foreground border-b border-border pb-1">
                          Replies ({ticket.replies.length})
                        </h4>
                        {ticket.replies.map((reply, index) => (
                          <div key={reply.id || index}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${
                                reply.senderType === 'admin' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {reply.senderType === 'admin' ? 'Support Team' : 'You'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(reply.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className={`border rounded-lg p-3 ${
                              reply.senderType === 'admin' 
                                ? 'bg-green-500/5 border-green-500/30 ml-4' 
                                : 'bg-card border-border mr-4'
                            }`}>
                              <p className="text-sm text-foreground whitespace-pre-wrap">{reply.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply Button - Always visible and enabled unless ticket is closed */}
                    {!isClosed ? (
                      <button
                        onClick={() => handleReplyClick(ticket)}
                        className="w-full mt-4 px-4 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <Send className="h-4 w-4" />
                        Reply to this ticket
                      </button>
                    ) : (
                      <div className="mt-4 p-3 bg-gray-500/10 border border-gray-500/30 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">This ticket is closed</p>
                      </div>
                    )}
                    
                    {/* Quick reply hint */}
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Support team typically responds within 24 hours
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Stats */}
      {filteredTickets.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {filteredTickets.length} of {tickets.length} tickets
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/80 transition-opacity"
            onClick={() => {
              setSelectedTicket(null);
              setReplyText('');
            }}
          />
          
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="relative bg-card border-2 border-border rounded-t-xl sm:rounded-xl w-full max-w-lg overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b-2 border-border flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Reply to Ticket</h3>
                  <p className="text-xs text-muted-foreground mt-1">{selectedTicket.ticketId}</p>
                  <p className="text-xs text-muted-foreground">{selectedTicket.subject}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setReplyText('');
                  }}
                  className="p-2 hover:bg-secondary rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {/* Last Message Preview */}
                <div className="mb-4 bg-secondary/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Last message:</p>
                  {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                    <p className="text-sm text-foreground">
                      {selectedTicket.replies[selectedTicket.replies.length - 1].senderType === 'admin' 
                        ? 'Support: ' 
                        : 'You: '}
                      {selectedTicket.replies[selectedTicket.replies.length - 1].text.substring(0, 100)}
                      {selectedTicket.replies[selectedTicket.replies.length - 1].text.length > 100 ? '...' : ''}
                    </p>
                  ) : (
                    <p className="text-sm text-foreground">
                      You: {selectedTicket.description.substring(0, 100)}...
                    </p>
                  )}
                </div>
                
                {/* Reply Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Reply:
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={5}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    autoFocus
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t-2 border-border flex gap-2">
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setReplyText('');
                  }}
                  className="flex-1 py-3 border border-border rounded-lg text-sm font-medium hover:bg-secondary/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sendingReply}
                  className="flex-1 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Reply
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTickets;
